import { useParams, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { ArrowLeft, Clock } from 'lucide-react';
import { useArticle } from '@/hooks/useArticles';
import { useArticleInteractions } from '@/hooks/useArticleInteractions';
import { useAuthor } from '@/hooks/useAuthor';
import { BlogLayout } from '@/components/BlogLayout';
import { ArticleContent } from '@/components/ArticleContent';
import { ArticleStats } from '@/components/ArticleStats';
import { ReactionButton } from '@/components/ReactionButton';
import { CommentSection } from '@/components/CommentSection';
import { ZapButton } from '@/components/ZapButton';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { articleCoordinate, formatDate, readingTime } from '@/lib/articleUtils';
import { BLOG_OWNER_PUBKEY, BLOG_TITLE } from '@/lib/blogConfig';

function ArticleSkeleton() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-12 w-3/4" />
      <Skeleton className="h-4 w-40" />
      <Skeleton className="aspect-[16/8] w-full rounded-xl" />
      <div className="space-y-3 pt-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-4 w-full" />
        ))}
      </div>
    </div>
  );
}

const ArticlePage = () => {
  const { identifier: rawId } = useParams<{ identifier: string }>();
  const identifier = rawId ? decodeURIComponent(rawId) : undefined;

  const { data: article, isLoading, error } = useArticle(identifier);
  const coordinate = article ? articleCoordinate(article.event) : undefined;
  const { data: interactions, isLoading: interactionsLoading } =
    useArticleInteractions(coordinate);

  const author = useAuthor(BLOG_OWNER_PUBKEY);
  const authorMeta = author.data?.metadata;
  const authorName =
    authorMeta?.display_name || authorMeta?.name || 'Dikaios1517';

  useSeoMeta({
    title: article ? `${article.title} — ${BLOG_TITLE}` : BLOG_TITLE,
    description: article?.summary || 'A theology essay published on Nostr.',
    ogImage: article?.image ? sanitizeUrl(article.image) : undefined,
  });

  const scrollToComments = () => {
    document.getElementById('comments')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <BlogLayout>
        <ArticleSkeleton />
      </BlogLayout>
    );
  }

  if (error || !article) {
    return (
      <BlogLayout>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center space-y-4">
              <p className="text-muted-foreground">
                This article couldn't be found. It may have been removed or
                isn't available on the current relays.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to all articles
              </Link>
            </CardContent>
          </Card>
        </div>
      </BlogLayout>
    );
  }

  const image = article.image ? sanitizeUrl(article.image) : undefined;
  const mins = readingTime(article.event.content);

  return (
    <BlogLayout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          All articles
        </Link>

        {/* Title */}
        <header className="mt-6 space-y-5">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold leading-tight tracking-tight text-foreground">
            {article.title}
          </h1>

          {/* Author + meta */}
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={authorMeta?.picture} alt={authorName} />
              <AvatarFallback>{authorName.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="text-sm">
              <div className="font-medium text-foreground">{authorName}</div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <time>{formatDate(article.publishedAt)}</time>
                <span aria-hidden>·</span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {mins} min read
                </span>
              </div>
            </div>
          </div>

          {/* Title image */}
          {image && (
            <div className="overflow-hidden rounded-xl border border-border/60">
              <img
                src={image}
                alt=""
                className="w-full object-cover"
              />
            </div>
          )}

          {/* Stats bar (below title + image, above content) */}
          <div className="rounded-xl border border-border/60 bg-card/50 px-4 py-3">
            <ArticleStats
              commentCount={interactions?.commentCount ?? 0}
              reactionCount={interactions?.reactionCount ?? 0}
              zapCount={interactions?.zapCount ?? 0}
              zapTotal={interactions?.zapTotal ?? 0}
              isLoading={interactionsLoading}
              onCommentsClick={scrollToComments}
            />
          </div>
        </header>

        {/* Content */}
        <div className="mt-8">
          <ArticleContent content={article.event.content} />
        </div>

        {/* Engagement actions */}
        {coordinate && (
          <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-border/60 pt-6">
            <ReactionButton
              article={article.event}
              reactions={interactions?.reactions ?? []}
              coordinate={coordinate}
            />
            <div className="inline-flex items-center rounded-full border border-border bg-card/60 px-3 py-1.5">
              <ZapButton target={article.event} className="text-sm" />
            </div>
          </div>
        )}

        {/* Comments (bottom) */}
        {coordinate && (
          <div className="mt-10">
            <CommentSection
              article={article.event}
              coordinate={coordinate}
              comments={interactions?.comments ?? []}
              isLoading={interactionsLoading}
            />
          </div>
        )}
      </article>
    </BlogLayout>
  );
};

export default ArticlePage;
